import type { FilterValues } from '@/components/common/filters/types'
import type { ListCustomersParams } from '@/lib/api/customer-api'
import { amountFilter } from '@/components/common/filters/amount-filter'
import { defineListFilters } from '@/components/common/filters/core'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { toggleFilter } from '@/components/common/filters/toggle-filter'
import { useCustomerGroups } from '@/hooks/use-customer-query'
import { m } from '@/lib/paraglide/messages'

export const CUSTOMERS_PER_PAGE = 20

const SORTS: Record<
    string,
    Pick<ListCustomersParams, 'sort_by' | 'sort_order'>
> = {
    debt_desc: { sort_by: 'current_debt', sort_order: 'desc' },
    debt_asc: { sort_by: 'current_debt', sort_order: 'asc' },
}

/**
 * Bộ lọc danh sách khách, thêm "Nợ hiện tại Từ … Tới …" (ô số tự do) và sắp
 * xếp theo nợ như màn Khách hàng của KiotViet mà kế toán đang quen.
 */
export const CUSTOMER_LIST_FILTERS = defineListFilters({
    q: textFilter({
        label: m.customer_search_label,
        placeholder: m.customer_search,
    }),
    group_id: remoteSelectFilter({
        label: m.customer_group_filter_label,
        allLabel: m.customer_all_groups,
        useOptions: () => {
            const { data } = useCustomerGroups()
            return (data ?? []).map((g) => ({ value: g.id, label: g.name }))
        },
    }),
    has_debt: toggleFilter({ label: m.customer_debt_filter_has_debt }),
    debt_min: amountFilter({ label: m.customer_debt_min_label }),
    debt_max: amountFilter({ label: m.customer_debt_max_label }),
    sort: selectFilter({
        label: m.customer_sort_label,
        defaultValue: 'default',
        options: () => [
            { value: 'default', label: m.customer_sort_default() },
            { value: 'debt_desc', label: m.customer_sort_debt_desc() },
            { value: 'debt_asc', label: m.customer_sort_debt_asc() },
        ],
    }),
})

export type CustomerListValues = FilterValues<
    typeof CUSTOMER_LIST_FILTERS.fields
>
export type CustomerSort = CustomerListValues['sort']

/** Bộ lọc → tham số GET /customers. Dòng tổng nợ luôn đi kèm danh sách. */
export function customerListApiParams(
    values: CustomerListValues,
    page: number,
): ListCustomersParams {
    return {
        search: values.q || undefined,
        customer_group_id:
            values.group_id === 'all' ? undefined : values.group_id,
        has_debt: values.has_debt ? true : undefined,
        debt_min: values.debt_min === '' ? undefined : Number(values.debt_min),
        debt_max: values.debt_max === '' ? undefined : Number(values.debt_max),
        ...SORTS[values.sort],
        page,
        per_page: CUSTOMERS_PER_PAGE,
        with_totals: '1',
    }
}
